import { supabase } from '../lib/supabase';
import { ENV } from '../config/env';
import { 
  BusinessConsultantChat, 
  BusinessConsultantMessage,
  ConsultantType,
  CreateChatParams,
  CreateMessageParams,
  BusinessConsultantService as IBusinessConsultantService,
  SaveIdeaParams,
  BusinessIdea
} from '../types/businessConsultant';
import { 
  createAppError, 
  ErrorCategory,
  withErrorHandling 
} from '../utils/errorHandler';
import { validateChatResponse } from '../utils/dataValidation';
import { knowledgeService } from './knowledgeService';

/**
 * Implementation of the Business Consultant Service
 * Handles interactions with Supabase and the Anthropic API for the virtual business consultant
 */
export class BusinessConsultantService implements IBusinessConsultantService {
  
  // Get the user ID from Supabase - standardized across services
  private async getUserId(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      const userId = data.session?.user?.id || '';
      if (!userId) {
        console.error('Error: Missing user ID in session');
      }
      return userId;
    } catch (e) {
      console.error('Error getting Supabase user ID:', e);
      return '';
    }
  }

  private normalizeToolResult(toolCall: any): Record<string, any> | null {
    if (!toolCall) return null;
    const raw = toolCall.result ?? toolCall;
    if (!raw) return null;

    if (typeof raw !== 'object') {
      return {
        success: true,
        action: toolCall.tool || toolCall.name || 'tool_action',
        message: String(raw)
      };
    }

    const action = raw.action || toolCall.tool || toolCall.name || 'tool_action';
    const success = typeof raw.success === 'boolean' ? raw.success : true;
    return {
      ...raw,
      action,
      success
    };
  }
  
  // Validate authentication before proceeding with operations
  private async validateAuthentication(): Promise<string> {
    const userId = await this.getUserId();
    if (!userId) {
      throw createAppError('User is not authenticated', ErrorCategory.AUTHENTICATION);
    }
    return userId;
  }
  
  // Get authorization token for API calls
  private async getAuthToken(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token || '';
    } catch (e) {
      console.error('Error getting auth token:', e);
      return '';
    }
  }
  
  /**
   * Get the user's consent status for the Business Consultant module
   */
  async getConsentStatus(): Promise<boolean> {
    return withErrorHandling(async () => {
      try {
        // Get user ID first to ensure authentication
        const userId = await this.getUserId();
        if (!userId) {
          console.warn('No user ID available when checking consent status');
          return false;
        }
        
        const { data, error } = await supabase
          .from('business_consultant_consents')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking consent status:', error);
          return false;
        }

        return data?.has_accepted || false;
      } catch (err) {
        console.error('Error in getConsentStatus:', err);
        return false;
      }
    }, { service: 'BusinessConsultantService', method: 'getConsentStatus' }, false);
  }

  /**
   * Record the user's consent status for the Business Consultant module
   */
  async recordConsent(hasConsented: boolean): Promise<void> {
    return withErrorHandling(async () => {
      try {
        // Use the standard method to validate authentication
        const userId = await this.validateAuthentication();

        const { error } = await supabase
          .from('business_consultant_consents')
          .upsert({
            user_id: userId,
            has_accepted: hasConsented,
            accepted_at: new Date().toISOString()
          });

          if (error) {
          console.error('Error recording consent:', error);
          throw createAppError('Failed to record consent status', ErrorCategory.STORAGE, undefined, undefined, { error });
        }
      } catch (err: any) {
        console.error('Error in recordConsent:', err);
        // Re-throw error if it's already an AppError, otherwise wrap it
        if (err?.name === 'AppError') {
          throw err;
        }
        throw createAppError('Failed to record consent status', ErrorCategory.UNKNOWN, undefined, undefined, { originalError: err });
      }
    }, { service: 'BusinessConsultantService', method: 'recordConsent' });
  }

  /**
   * Get all chats for the current user
   */
  async getChats(): Promise<BusinessConsultantChat[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID using standardized method and validate authentication
        const userId = await this.validateAuthentication();
        
        console.log('Fetching business consultant chats for user:', { userId });
        
        const { data, error } = await supabase
          .from('business_consultant_chats')
          .select('*')
          .eq('user_id', userId) // Ensure we only fetch chats for the current user
          .eq('is_archived', false)
          .order('updated_at', { ascending: false });

        if (error) {
          console.error('Error fetching chats:', error);
          throw createAppError(
            'Failed to fetch chats', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading your conversations',
            { error }
          );
        }

        return data || [];
      } catch (err: any) {
        console.error('Error in getChats:', err);
        
        // Re-throw AppErrors, wrap others
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch chats', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an error loading your conversations',
          { originalError: err }
        );
      }
    }, { service: 'BusinessConsultantService', method: 'getChats' });
  }

  /**
   * Get a specific chat by ID
   */
  async getChatById(chatId: string): Promise<BusinessConsultantChat | null> {
    return withErrorHandling(async () => {
      try {
        // Get user ID using standardized method and validate authentication
        const userId = await this.validateAuthentication();
        
        console.log('Fetching business consultant chat by ID:', { chatId, userId });
        
        const { data, error } = await supabase
          .from('business_consultant_chats')
          .select('*')
          .eq('id', chatId)
          .eq('user_id', userId) // Security: ensure user can only access their own chats
          .single();

        if (error) {
          console.error('Error fetching chat:', error);
          throw createAppError(
            'Failed to fetch chat', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading the conversation',
            { error }
          );
        }

        return data;
      } catch (err: any) {
        console.error('Error in getChatById:', err);
        
        // Re-throw AppErrors, wrap others
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch chat', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an error loading the conversation',
          { originalError: err }
        );
      }
    }, { service: 'BusinessConsultantService', method: 'getChatById' });
  }

  /**
   * Create a new chat with the specified consultant type
   */
  async createChat(params: CreateChatParams): Promise<BusinessConsultantChat> {
    return withErrorHandling(async () => {
      try {
        // Get user ID using our standardized method
        const userId = await this.validateAuthentication();
  
        console.log('Creating new business consultant chat:', {
          consultantType: params.consultant_type,
          chatName: params.chat_name,
          userId
        });
  
        const newChat = {
          user_id: userId,
          chat_name: params.chat_name,
          consultant_type: params.consultant_type,
          business_id: params.business_id || null,
          idea_id: params.idea_id || null,
          business_plan_id: params.business_plan_id || null,
          tokens_used: 0,
          is_archived: false
        };
        
        const { data, error } = await supabase
          .from('business_consultant_chats')
          .insert(newChat)
          .select('*')
          .single();
  
        if (error) {
          console.error('Error creating chat:', error);
          throw createAppError(
            `Failed to create chat: ${error.message}`,
            ErrorCategory.STORAGE,
            undefined,
            'Unable to create a new conversation. Please try again.',
            { error }
          );
        }
  
        if (!data) {
          throw createAppError(
            'Failed to create chat: No data returned',
            ErrorCategory.STORAGE,
            undefined,
            'Unable to create a new conversation. Please try again.'
          );
        }
        
        console.log('Successfully created chat:', { chatId: data.id });
        
        // Create welcome message for new chat
        await this.createMessage({
          chat_id: data.id,
          role: 'assistant',
          content: this.getWelcomeMessage(params.consultant_type)
        });
  
        // Return data with correct property mapping
        return {
          ...data,
          total_tokens_used: data.tokens_used // Map database column to interface property
        };
      } catch (err: any) {
        console.error('Error in createChat:', err);
        
        // Re-throw AppErrors, wrap others
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          `Failed to create chat: ${err?.message || 'Unknown error'}`,
          ErrorCategory.UNKNOWN,
          undefined,
          'Unable to create a new conversation. Please try again.',
          { originalError: err }
        );
      }
    }, { service: 'BusinessConsultantService', method: 'createChat' });
  }

  /**
   * Archive an existing chat (soft delete)
   */
  async archiveChat(chatId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('business_consultant_chats')
        .update({ is_archived: true })
        .eq('id', chatId);

      if (error) {
        console.error('Error archiving chat:', error);
        throw new Error('Failed to archive chat');
      }
    } catch (err) {
      console.error('Error in archiveChat:', err);
      throw new Error('Failed to archive chat');
    }
  }

  /**
   * Get all messages for a specific chat
   */
  async getMessagesByChatId(chatId: string): Promise<BusinessConsultantMessage[]> {
    try {
      const { data, error } = await supabase
        .from('business_consultant_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        throw new Error('Failed to fetch messages');
      }

      return data || [];
    } catch (err) {
      console.error('Error in getMessagesByChatId:', err);
      throw new Error('Failed to fetch messages');
    }
  }

  /**
   * Create a new message in a chat
   */
  async createMessage(params: CreateMessageParams): Promise<BusinessConsultantMessage> {
    try {
      const newMessage = {
        ...params,
        tokens_used: 0
      };
      
      const { data, error } = await supabase
        .from('business_consultant_messages')
        .insert(newMessage)
        .select('*')
        .single();

      if (error) {
        console.error('Error creating message:', error);
        throw new Error('Failed to create message');
      }

      if (!data) {
        throw new Error('Failed to create message: No data returned');
      }

      // Update chat timestamp
      await supabase
        .from('business_consultant_chats')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('id', params.chat_id);

      return data;
    } catch (err) {
      console.error('Error in createMessage:', err);
      throw new Error('Failed to create message');
    }
  }
  
  /**
   * Send a user message to a chat and get an AI response
   */
  async sendMessageToChatAndGetResponse(
    chatId: string,
    content: string,
    includeBusiness: boolean = false,
    businessId?: string,
    options?: { skipUserMessageCreate?: boolean }
  ): Promise<BusinessConsultantMessage> {
    return withErrorHandling(async () => {
      try {
        // First validate auth using our standardized method
        const userId = await this.validateAuthentication();
        const authToken = await this.getAuthToken();
        
        if (!authToken) {
          throw createAppError('Missing authentication token', ErrorCategory.AUTHENTICATION);
        }
        
        // Get chat details to know which consultant type
        const chat = await this.getChatById(chatId);
        if (!chat) {
          throw createAppError('Chat not found', ErrorCategory.VALIDATION, undefined, 'Chat not found. Please try creating a new conversation.');
        }

        // Log request for debugging
        console.log('Sending message to API:', {
          chatId,
          consultantType: chat.consultant_type,
          includeBusiness,
          timestamp: new Date().toISOString()
        });
        
        if (!options?.skipUserMessageCreate) {
          await this.createMessage({
            chat_id: chatId,
            role: 'user',
            content: content
          });
        }

        // Search knowledge base intelligently based on user's actual query
        let knowledgeContext = '';
        if (includeBusiness && (businessId || (chat as any).business_id)) {
          const effectiveBusinessId = businessId || (chat as any).business_id;
          
          // SMART: Search knowledge base using the user's actual question
          const contexts = await knowledgeService.searchKnowledge(
            userId,
            content, // Use the actual user message to search
            effectiveBusinessId,
            5 // Get top 5 most relevant documents
          );
          
          if (contexts.length > 0) {
            knowledgeContext = knowledgeService.formatKnowledgeForPrompt(contexts);
            console.log(`[BusinessConsultant] Including ${contexts.length} relevant knowledge documents for query: "${content.slice(0, 50)}..."`);
          } else {
            console.log(`[BusinessConsultant] No relevant knowledge found for query`);
          }
        }

        // Enhance message with knowledge context
        const enhancedMessage = knowledgeContext 
          ? `${content}${knowledgeContext}` 
          : content;
      
        // Prepare API request with consistent structure
        const response = await fetch(`${ENV.API_URL}/api/business-consultant/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            userId: userId,
            chat_id: chatId,
            message: enhancedMessage,
            include_business_data: includeBusiness,
            consultant_type: chat.consultant_type,
            // Prefer explicitly provided businessId, else fall back to chat's stored business_id
            business_id: businessId || (chat as any).business_id || undefined,
            has_knowledge_context: knowledgeContext.length > 0
          }),
        });

        if (!response.ok) {
          const status = response.status;
          let errorMessage = `API error ${status}`;
          
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
            console.error('API response error:', { status, errorData });
          } catch (e) {
            const errorText = await response.text();
            console.error('API response error (raw):', { status, errorText });
          }
          
          throw createAppError(
            `Failed to get AI response: ${errorMessage}`, 
            ErrorCategory.API, 
            status,
            'Failed to get response from the AI assistant. Please try again later.'
          );
        }

        const responseData = await response.json();
        const validatedResponse = validateChatResponse(responseData);
        const toolCalls = Array.isArray(validatedResponse.tool_calls) ? validatedResponse.tool_calls : [];
        console.log('Received API response:', { success: true, hasMessage: !!validatedResponse.message, toolCalls: toolCalls.length });

        // Create AI message in database
        const aiMessage = await this.createMessage({
          chat_id: chatId,
          role: 'assistant',
          content: validatedResponse.message
        });

        // Persist tool results from backend for UI rendering (backend executes tools)
        if (toolCalls.length > 0) {
          for (const toolCall of toolCalls) {
            const normalized = this.normalizeToolResult(toolCall);
            if (!normalized) continue;
            try {
              await this.createMessage({
                chat_id: chatId,
                role: 'assistant',
                content: JSON.stringify(normalized)
              });
              const missingFields = Array.isArray((normalized as any).missing_fields)
                ? (normalized as any).missing_fields
                : [];
              const followUpQuestions = Array.isArray((normalized as any).follow_up_questions)
                ? (normalized as any).follow_up_questions
                : [];
              const followUpMessage = missingFields.length > 0
                ? (normalized.message || normalized.note || `Please provide ${missingFields.join(', ')} to continue.`)
                : (followUpQuestions.length > 0 ? followUpQuestions.join(' ') : null);
              if (followUpMessage) {
                await this.createMessage({
                  chat_id: chatId,
                  role: 'assistant',
                  content: followUpMessage
                });
              }
            } catch (toolErr) {
              console.error('[BusinessConsultant] Failed to persist tool result message:', toolErr);
            }
          }
        }

        // Update token counts
        if (chat.total_tokens_used !== undefined && validatedResponse.tokens_used) {
          await supabase
            .from('business_consultant_chats')
            .update({
              total_tokens_used: chat.total_tokens_used + validatedResponse.tokens_used
            })
            .eq('id', chatId);
        }

        // Update the message with token count
        if (validatedResponse.tokens_used) {
          await supabase
            .from('business_consultant_messages')
            .update({
              tokens_used: validatedResponse.tokens_used
            })
            .eq('id', aiMessage.id);
        }

        return aiMessage;
      } catch (err: any) {
        console.error('Error sending message:', err);
        
        // Re-throw AppErrors, wrap other errors
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to process message', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an error sending your message. Please try again.',
          { originalError: err }
        );
      }
    }, { service: 'BusinessConsultantService', method: 'sendMessageToChatAndGetResponse' });
  }
  
  /**
   * Execute tool actions returned by AI
   */
  private async executeToolAction(toolResult: any): Promise<void> {
    if (!toolResult || !toolResult.action) {
      console.warn('[BusinessConsultant] Tool result missing action:', toolResult);
      return;
    }
    
    const { action } = toolResult;
    console.log(`[BusinessConsultant] Executing action: ${action}`);
    
    switch (action) {
      case 'post_to_linkedin':
        await this.postToLinkedIn(toolResult.content, toolResult.when);
        break;
        
      case 'generate_image':
        await this.generateImage(toolResult.prompt, toolResult.width, toolResult.height);
        break;
        
      case 'send_email':
        await this.sendEmail(toolResult.to, toolResult.subject, toolResult.text);
        break;
        
      case 'create_draft_post':
        await this.createDraftPost(toolResult.platform, toolResult.content);
        break;
        
      case 'get_seo_analytics':
        return await this.getSEOAnalytics(toolResult.date_range);
        
      case 'generate_seo_report':
        return await this.generateSEOReport(toolResult.report_type);
        
      case 'get_keyword_rankings':
        return await this.getKeywordRankings(toolResult.keywords, toolResult.limit);
        
      case 'get_staff_report':
        return await this.getStaffReport(toolResult.report_type, toolResult.time_period);
        
      case 'get_recruitment_report':
        return await this.getRecruitmentReport(toolResult.report_type, toolResult.job_id);
        
      case 'get_crm_report':
        return await this.getCRMReport(toolResult.report_type, toolResult.time_period);
        
      case 'get_investor_report':
        return await this.getInvestorReport(toolResult.report_type);
        
      case 'get_operations_report':
        return await this.getOperationsReport(toolResult.report_type);
        
      case 'generate_business_summary':
        return await this.generateBusinessSummary(toolResult.frequency, toolResult.focus_areas);
        
      case 'get_holiday_request_detail':
        return await this.getHolidayRequestDetail(toolResult.holiday_id);
        
      case 'approve_holiday_request':
        return await this.approveHolidayRequest(toolResult.holiday_id, toolResult.notes);
        
      case 'deny_holiday_request':
        return await this.denyHolidayRequest(toolResult.holiday_id, toolResult.reason);
        
      case 'mark_holiday_for_review':
        return await this.markHolidayForReview(toolResult.holiday_id, toolResult.notes);
        
      case 'get_project_management_report':
        return await this.getProjectManagementReport(toolResult.report_type, toolResult.project_id);
        
      case 'get_risk_assessment':
        return await this.getRiskAssessment(toolResult.focus_area);
        
      default:
        console.warn(`[BusinessConsultant] Unknown action: ${action}`);
    }
  }
  
  /**
   * Post to LinkedIn
   */
  private async postToLinkedIn(content: string, when: string = 'now'): Promise<void> {
    try {
      const { SocialMediaPlannerService } = await import('./socialMediaPlannerService');
      const planner = new SocialMediaPlannerService();
      
      // Create draft first
      const draft = await planner.createPost({
        content,
        media_urls: [],
        platforms: ['linkedin'],
        status: 'draft',
        platform_details: []
      } as any);
      
      console.log('[BusinessConsultant] LinkedIn draft created:', draft.id);
      
      // If "now", publish immediately
      if (when === 'now') {
        const { socialSchedulerService } = await import('./socialSchedulerService');
        await socialSchedulerService.postLinkedInNow({
          content,
          when: 'now',
          media_urls: []
        });
        console.log('[BusinessConsultant] LinkedIn post published');
      }
    } catch (err) {
      console.error('[BusinessConsultant] LinkedIn posting failed:', err);
      throw err;
    }
  }
  
  /**
   * Generate image
   */
  private async generateImage(prompt: string, width?: number, height?: number): Promise<void> {
    try {
      const { ImageGenerationService } = await import('./imageGenerationService');
      const result = await ImageGenerationService.generateImage(prompt, { width, height });
      console.log('[BusinessConsultant] Image generated:', result);
    } catch (err) {
      console.error('[BusinessConsultant] Image generation failed:', err);
      throw err;
    }
  }
  
  /**
   * Send email
   */
  private async sendEmail(to: string, subject: string, text: string): Promise<void> {
    try {
      const emailService = (await import('./emailService')).default;
      await emailService.sendEmail({ to, subject, text });
      console.log('[BusinessConsultant] Email sent to:', to);
    } catch (err) {
      console.error('[BusinessConsultant] Email sending failed:', err);
      throw err;
    }
  }
  
  /**
   * Create draft post
   */
  private async createDraftPost(platform: string, content: string): Promise<void> {
    try {
      const { SocialMediaPlannerService } = await import('./socialMediaPlannerService');
      const planner = new SocialMediaPlannerService();
      
      const draft = await planner.createPost({
        content,
        media_urls: [],
        platforms: [platform],
        status: 'draft',
        platform_details: []
      } as any);
      
      console.log(`[BusinessConsultant] ${platform} draft created:`, draft.id);
    } catch (err) {
      console.error(`[BusinessConsultant] ${platform} draft creation failed:`, err);
      throw err;
    }
  }
  
  /**
   * Get SEO analytics data
   */
  private async getSEOAnalytics(dateRange: string = '30days'): Promise<any> {
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      
      const response = await fetch(`${ENV.API_URL}/api/marketing/web-traffic-analytics?dateRange=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch SEO analytics');
      }
      
      const data = await response.json();
      console.log('[BusinessConsultant] SEO analytics retrieved:', data);
      return data;
    } catch (err) {
      console.error('[BusinessConsultant] SEO analytics failed:', err);
      throw err;
    }
  }
  
  /**
   * Generate SEO progress report
   */
  private async generateSEOReport(reportType: string = 'bi-weekly'): Promise<any> {
    try {
      // Get analytics data
      const dateRange = reportType === 'weekly' ? '7days' : reportType === 'monthly' ? '30days' : '30days';
      const analytics = await this.getSEOAnalytics(dateRange);
      
      // Format into a comprehensive report
      const report = {
        report_type: reportType,
        date_range: dateRange,
        summary: {
          total_clicks: analytics.combined?.reduce((sum: number, k: any) => sum + k.totalClicks, 0) || 0,
          total_impressions: analytics.combined?.reduce((sum: number, k: any) => sum + k.totalImpressions, 0) || 0,
          average_position: analytics.combined?.length > 0 
            ? (analytics.combined.reduce((sum: number, k: any) => sum + (k.googlePosition || k.bingPosition || 0), 0) / analytics.combined.length).toFixed(2)
            : 0,
          top_keywords: analytics.combined?.slice(0, 10) || []
        },
        google: {
          clicks: analytics.google?.reduce((sum: number, k: any) => sum + k.googleClicks, 0) || 0,
          impressions: analytics.google?.reduce((sum: number, k: any) => sum + k.googleImpressions, 0) || 0,
          top_keywords: analytics.google?.slice(0, 5) || []
        },
        bing: {
          clicks: analytics.bing?.reduce((sum: number, k: any) => sum + k.bingClicks, 0) || 0,
          impressions: analytics.bing?.reduce((sum: number, k: any) => sum + k.bingImpressions, 0) || 0,
          top_keywords: analytics.bing?.slice(0, 5) || []
        },
        insights: this.generateSEOInsights(analytics)
      };
      
      console.log('[BusinessConsultant] SEO report generated:', report);
      return report;
    } catch (err) {
      console.error('[BusinessConsultant] SEO report generation failed:', err);
      throw err;
    }
  }
  
  /**
   * Get keyword rankings
   */
  private async getKeywordRankings(keywords?: string[], limit: number = 20): Promise<any> {
    try {
      const analytics = await this.getSEOAnalytics('30days');
      
      let keywordData = analytics.combined || [];
      
      // Filter by specific keywords if provided
      if (keywords && keywords.length > 0) {
        keywordData = keywordData.filter((k: any) => 
          keywords.some(keyword => k.keyword?.toLowerCase().includes(keyword.toLowerCase()))
        );
      }
      
      // Sort by total clicks and limit
      keywordData = keywordData
        .sort((a: any, b: any) => b.totalClicks - a.totalClicks)
        .slice(0, limit);
      
      console.log('[BusinessConsultant] Keyword rankings retrieved:', keywordData.length);
      return {
        keywords: keywordData,
        total_keywords: analytics.combined?.length || 0
      };
    } catch (err) {
      console.error('[BusinessConsultant] Keyword rankings failed:', err);
      throw err;
    }
  }
  
  /**
   * Generate insights from SEO data
   */
  private generateSEOInsights(analytics: any): string[] {
    const insights: string[] = [];
    
    if (!analytics.combined || analytics.combined.length === 0) {
      insights.push('No SEO data available yet. Connect Google Search Console or Bing Webmaster Tools to start tracking.');
      return insights;
    }
    
    const totalClicks = analytics.combined.reduce((sum: number, k: any) => sum + k.totalClicks, 0);
    const totalImpressions = analytics.combined.reduce((sum: number, k: any) => sum + k.totalImpressions, 0);
    const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;
    
    insights.push(`Your site received ${totalClicks} clicks from ${totalImpressions} impressions (${avgCTR}% CTR)`);
    
    // Top performing keyword
    if (analytics.combined.length > 0) {
      const topKeyword = analytics.combined[0];
      insights.push(`Top keyword: "${topKeyword.keyword}" with ${topKeyword.totalClicks} clicks`);
    }
    
    // CTR analysis
    if (parseFloat(avgCTR as string) < 2) {
      insights.push('Low CTR detected. Consider improving meta titles and descriptions for better click-through rates.');
    } else if (parseFloat(avgCTR as string) > 5) {
      insights.push('Excellent CTR! Your content is resonating well with searchers.');
    }
    
    // Position analysis
    const avgPosition = analytics.combined.reduce((sum: number, k: any) => sum + (k.googlePosition || k.bingPosition || 0), 0) / analytics.combined.length;
    if (avgPosition > 10) {
      insights.push(`Average position is ${avgPosition.toFixed(1)}. Focus on improving content quality and backlinks to rank higher.`);
    } else if (avgPosition <= 3) {
      insights.push(`Great rankings! Average position: ${avgPosition.toFixed(1)}. Maintain and expand on your top content.`);
    }
    
    return insights;
  }
  
  /**
   * Get Staff Management Report
   */
  private async getStaffReport(reportType: string = 'overview', timePeriod: string = 'month'): Promise<any> {
    try {
      const staffService = (await import('./staffManagementService')).default;
      
      const { data: staff } = await staffService.listStaff();
      const { data: holidays } = await staffService.listHolidays();
      const { data: shifts } = await staffService.listShifts();
      
      const activeStaff = staff.filter((s: any) => s.status === 'active');
      const pendingHolidays = holidays.filter((h: any) => h.status === 'pending');
      const approvedHolidays = holidays.filter((h: any) => h.status === 'approved');
      
      const report = {
        report_type: reportType,
        time_period: timePeriod,
        headcount: {
          total: staff.length,
          active: activeStaff.length,
          by_department: this.groupBy(activeStaff, 'department')
        },
        holidays: {
          pending_requests: pendingHolidays.length,
          approved_upcoming: approvedHolidays.length,
          requests: pendingHolidays.map((h: any) => ({
            id: h.id,
            staff_id: h.staff_id,
            staff: `${h.staff_first_name} ${h.staff_last_name}`,
            position: h.staff_position,
            department: h.staff_department,
            dates: `${h.start_date} to ${h.end_date}`,
            start_date: h.start_date,
            end_date: h.end_date,
            days: h.days_count,
            type: h.type,
            reason: h.reason,
            requested_at: h.created_at
          }))
        },
        coverage: {
          weekly_shifts: shifts.length,
          coverage_by_day: this.calculateShiftCoverage(shifts, staff)
        }
      };
      
      console.log('[BusinessConsultant] Staff report generated:', report);
      return report;
    } catch (err) {
      console.error('[BusinessConsultant] Staff report failed:', err);
      throw err;
    }
  }
  
  /**
   * Get Recruitment Report
   */
  private async getRecruitmentReport(reportType: string = 'overview', jobId?: string): Promise<any> {
    try {
      const recruitmentService = await import('./recruitmentService');
      
      const [jobs, applications, , analytics] = await Promise.all([
        recruitmentService.getJobs(),
        recruitmentService.getApplications(),
        recruitmentService.getCandidates(),
        recruitmentService.getRecruitmentAnalytics()
      ]);
      
      const openJobs = jobs.filter((j: any) => j.status === 'open');
      const newApplications = applications.filter((a: any) => a.stage === 'applied');
      const interviewStage = applications.filter((a: any) => a.stage === 'interview');
      
      const report = {
        report_type: reportType,
        job_id: jobId,
        overview: {
          open_positions: openJobs.length,
          total_applicants: applications.length,
          new_this_week: newApplications.length,
          in_interview: interviewStage.length
        },
        pipeline: {
          by_stage: this.groupBy(applications, 'stage'),
          by_job: openJobs.map((job: any) => ({
            title: job.title,
            location: job.location,
            applicants: applications.filter((a: any) => a.job_id === job.id).length
          }))
        },
        analytics: analytics || {},
        recent_applications: applications.slice(0, 5).map((a: any) => ({
          candidate: a.candidate_name,
          job: a.job_title,
          stage: a.stage,
          applied_date: a.created_at
        }))
      };
      
      console.log('[BusinessConsultant] Recruitment report generated:', report);
      return report;
    } catch (err) {
      console.error('[BusinessConsultant] Recruitment report failed:', err);
      throw err;
    }
  }
  
  /**
   * Get CRM Report
   */
  private async getCRMReport(reportType: string = 'overview', timePeriod: string = 'month'): Promise<any> {
    try {
      const dealsService = (await import('./crm/dealsService')).default;
      const contactsService = (await import('./crm/contactsService')).default;
      const companiesService = (await import('./crm/companiesService')).default;
      
      // Get organization_id from user session
      const { data: userData } = await supabase.auth.getUser();
      const organizationId = userData?.user?.user_metadata?.organization_id || '';
      
      const { data: deals } = await dealsService.list({ organization_id: organizationId, limit: 100 });
      const { data: contacts } = await contactsService.list({ organization_id: organizationId, limit: 100 });
      const { data: companies } = await companiesService.list({ organization_id: organizationId, limit: 100 });
      
      const openDeals = deals.filter((d: any) => d.status !== 'won' && d.status !== 'lost');
      const wonDeals = deals.filter((d: any) => d.status === 'won');
      const totalValue = openDeals.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);
      const wonValue = wonDeals.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);
      
      const report = {
        report_type: reportType,
        time_period: timePeriod,
        pipeline: {
          total_deals: deals.length,
          open_deals: openDeals.length,
          total_value: totalValue,
          weighted_value: openDeals.reduce((sum: number, d: any) => sum + ((d.amount || 0) * (d.probability || 0) / 100), 0)
        },
        revenue: {
          won_deals: wonDeals.length,
          won_value: wonValue,
          win_rate: deals.length > 0 ? ((wonDeals.length / deals.length) * 100).toFixed(1) : 0
        },
        customers: {
          total_contacts: contacts.length,
          total_companies: companies.length
        },
        top_deals: openDeals
          .sort((a: any, b: any) => (b.amount || 0) - (a.amount || 0))
          .slice(0, 5)
          .map((d: any) => ({
            title: d.title,
            company: d.company_name,
            amount: d.amount,
            stage: d.stage_name,
            probability: d.probability
          }))
      };
      
      console.log('[BusinessConsultant] CRM report generated:', report);
      return report;
    } catch (err) {
      console.error('[BusinessConsultant] CRM report failed:', err);
      throw err;
    }
  }
  
  /**
   * Get Investor Report
   */
  private async getInvestorReport(reportType: string = 'overview'): Promise<any> {
    try {
      // Investor CRM uses similar structure to customer CRM
      const dealsService = (await import('./crm/dealsService')).default;
      const contactsService = (await import('./crm/contactsService')).default;
      
      // Get organization_id from user session
      const { data: userData } = await supabase.auth.getUser();
      const organizationId = userData?.user?.user_metadata?.organization_id || '';
      
      // Filter for investor-related data
      const { data: deals } = await dealsService.list({ organization_id: organizationId, limit: 100 });
      const { data: contacts } = await contactsService.list({ organization_id: organizationId, limit: 100 });
      
      const investorDeals = deals.filter((d: any) => d.deal_type === 'investment' || d.stage_name?.includes('Funding'));
      const activeInvestors = contacts.filter((c: any) => c.type === 'investor');
      
      const report = {
        report_type: reportType,
        overview: {
          total_investors: activeInvestors.length,
          active_conversations: investorDeals.filter((d: any) => d.status === 'open').length,
          total_raised: investorDeals
            .filter((d: any) => d.status === 'won')
            .reduce((sum: number, d: any) => sum + (d.amount || 0), 0)
        },
        pipeline: investorDeals.map((d: any) => ({
          investor: d.company_name || d.contact_name,
          stage: d.stage_name,
          amount: d.amount,
          probability: d.probability
        })),
        recent_activity: investorDeals.slice(0, 5).map((d: any) => ({
          investor: d.company_name,
          action: d.stage_name,
          date: d.updated_at
        }))
      };
      
      console.log('[BusinessConsultant] Investor report generated:', report);
      return report;
    } catch (err) {
      console.error('[BusinessConsultant] Investor report failed:', err);
      throw err;
    }
  }
  
  /**
   * Get Operations Report
   */
  private async getOperationsReport(reportType: string = 'overview'): Promise<any> {
    try {
      // Operations data - suppliers, risks, etc.
      const { data: session } = await supabase.auth.getSession();
      
      // Fetch operations data if available
      const report = {
        report_type: reportType,
        overview: {
          message: 'Operations reporting available for supplier management, risk tracking, and procurement'
        },
        suppliers: {
          total: 0,
          active: 0,
          pending_orders: 0
        },
        risks: {
          total: 0,
          high_priority: 0,
          medium_priority: 0
        }
      };
      
      console.log('[BusinessConsultant] Operations report generated:', report);
      return report;
    } catch (err) {
      console.error('[BusinessConsultant] Operations report failed:', err);
      throw err;
    }
  }
  
  /**
   * Generate Comprehensive Business Summary
   */
  private async generateBusinessSummary(frequency: string = 'weekly', focusAreas?: string[]): Promise<any> {
    try {
      console.log('[BusinessConsultant] Generating business summary:', frequency, focusAreas);
      
      // Fetch all reports in parallel
      const [staffReport, recruitmentReport, crmReport, seoReport] = await Promise.all([
        this.getStaffReport('overview', 'week').catch(() => null),
        this.getRecruitmentReport('overview').catch(() => null),
        this.getCRMReport('overview', 'week').catch(() => null),
        this.getSEOAnalytics('7days').catch(() => null)
      ]);
      
      const summary = {
        frequency,
        generated_at: new Date().toISOString(),
        focus_areas: focusAreas || ['sales', 'hiring', 'staff', 'marketing'],
        highlights: {
          staff: staffReport ? {
            headcount: staffReport.headcount.active,
            pending_holidays: staffReport.holidays.pending_requests
          } : null,
          recruitment: recruitmentReport ? {
            open_positions: recruitmentReport.overview.open_positions,
            new_applicants: recruitmentReport.overview.new_this_week
          } : null,
          sales: crmReport ? {
            open_deals: crmReport.pipeline.open_deals,
            pipeline_value: crmReport.pipeline.total_value,
            win_rate: crmReport.revenue.win_rate
          } : null,
          marketing: seoReport ? {
            weekly_clicks: seoReport.combined?.reduce((sum: number, k: any) => sum + k.totalClicks, 0) || 0,
            total_impressions: seoReport.combined?.reduce((sum: number, k: any) => sum + k.totalImpressions, 0) || 0
          } : null
        },
        detailed_reports: {
          staff: staffReport,
          recruitment: recruitmentReport,
          crm: crmReport,
          seo: seoReport
        }
      };
      
      console.log('[BusinessConsultant] Business summary generated:', summary);
      return summary;
    } catch (err) {
      console.error('[BusinessConsultant] Business summary failed:', err);
      throw err;
    }
  }
  
  /**
   * Helper: Group array by key
   */
  private groupBy(array: any[], key: string): Record<string, number> {
    return array.reduce((acc, item) => {
      const value = item[key] || 'Unknown';
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});
  }
  
  /**
   * Helper: Calculate shift coverage
   */
  private calculateShiftCoverage(shifts: any[], staff: any[]): Record<string, number> {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const coverage: Record<string, number> = {};
    
    days.forEach((day, index) => {
      const dayShifts = shifts.filter((s: any) => s.day_of_week === index);
      coverage[day] = dayShifts.length;
    });
    
    return coverage;
  }
  
  /**
   * Save a business idea for the current user
   */
  async saveIdea(params: SaveIdeaParams): Promise<BusinessIdea> {
    return withErrorHandling(async () => {
      try {
        // Validate required fields
        if (!params?.chat_id || !params?.title) {
          throw createAppError('Missing required fields for saving idea', ErrorCategory.VALIDATION, undefined, 'Please provide a valid idea title.');
        }

        // Validate authentication
        const userId = await this.validateAuthentication();

        const payload = {
          user_id: userId,
          chat_id: params.chat_id,
          title: params.title.trim(),
          summary: params.summary?.trim() || null,
          source_message_id: params.source_message_id || null,
        };

        const { data, error } = await supabase
          .from('business_ideas')
          .insert(payload)
          .select('*')
          .single();

        if (error) {
          console.error('Error saving business idea:', error);
          throw createAppError('Failed to save idea', ErrorCategory.STORAGE, undefined, 'Could not save your idea. Please try again.', { error });
        }

        if (!data) {
          throw createAppError('No data returned while saving idea', ErrorCategory.STORAGE, undefined, 'Could not save your idea. Please try again.');
        }

        return data as BusinessIdea;
      } catch (err: any) {
        if (err?.name === 'AppError') throw err;
        throw createAppError('Failed to save idea', ErrorCategory.UNKNOWN, undefined, 'Could not save your idea. Please try again.', { originalError: err });
      }
    }, { service: 'BusinessConsultantService', method: 'saveIdea' });
  }
  
  /**
   * Helper method to get welcome message based on consultant type
   */
  private getWelcomeMessage(consultantType: ConsultantType): string {
    const disclaimerNote = `**Important:** My advice is AI-generated and should not replace professional consultation. Please verify any information before making business decisions.`;
    
    switch (consultantType) {
      case 'marketing':
        return `👋 Hello! I'm Keith, your Marketing Strategist. I specialize in digital marketing, brand positioning, and customer acquisition strategies. What marketing challenge can I assist you with today?\n\n${disclaimerNote}`;
      
      case 'finance':
        return `👋 Hello! I'm Carol, your Financial Advisor. I can help with financial planning, funding options, and optimizing your business financials. What financial question can I address for you today?\n\n${disclaimerNote}`;
      
      case 'strategy':
        return `👋 Hello! I'm Keith, your Business Strategy & Growth Consultant. I specialize in business strategy, planning, competitive analysis, and sustainable growth strategies. What strategic challenge are you facing today?\n\n${disclaimerNote}`;
      
      default:
        return `👋 Hello! I'm your Virtual Business Consultant. How can I help you today?\n\n${disclaimerNote}`;
    }
  }
  
  /**
   * Get detailed holiday request with coverage analysis
   */
  private async getHolidayRequestDetail(holidayId: string): Promise<any> {
    try {
      const staffService = (await import('./staffManagementService')).default;
      
      // Get the specific holiday request
      const { data: holidays } = await staffService.listHolidays();
      const holiday: any = holidays.find((h: any) => h.id === holidayId);
      
      if (!holiday) {
        throw new Error('Holiday request not found');
      }
      
      // Get all staff and shifts for coverage analysis
      const { data: staff } = await staffService.listStaff();
      await staffService.listShifts();
      const { data: approvedHolidays } = await staffService.listHolidays();
      
      const requestingStaff = staff.find((s: any) => s.id === holiday.staff_id);
      
      // Analyze coverage during requested period
      const startDate = new Date(holiday.start_date);
      const endDate = new Date(holiday.end_date);
      
      // Find other staff in same department
      const sameDeptStaff = staff.filter((s: any) => 
        s.department === requestingStaff?.department && 
        s.id !== holiday.staff_id &&
        s.status === 'active'
      );
      
      // Find overlapping approved holidays
      const overlappingHolidays = approvedHolidays.filter((h: any) => {
        if (h.status !== 'Approved' || h.id === holidayId) return false;
        const hStart = new Date(h.start_date);
        const hEnd = new Date(h.end_date);
        return (hStart <= endDate && hEnd >= startDate);
      });
      
      // Calculate coverage
      const totalDeptStaff = sameDeptStaff.length + 1; // Include requesting staff
      const staffOnHoliday = overlappingHolidays.length + 1; // Include this request
      const staffAvailable = totalDeptStaff - staffOnHoliday;
      const coveragePercentage = totalDeptStaff > 0 ? (staffAvailable / totalDeptStaff) * 100 : 0;
      
      // AI Recommendation
      let recommendation = 'approve';
      let rationale = 'Good coverage available during requested period.';
      
      if (coveragePercentage < 50) {
        recommendation = 'deny';
        rationale = 'Insufficient staff coverage - would leave department understaffed.';
      } else if (coveragePercentage < 70) {
        recommendation = 'consider';
        rationale = 'Borderline coverage - review department workload before deciding.';
      }
      
      const detail = {
        holiday: {
          id: holiday.id,
          staff: `${holiday.staff_first_name} ${holiday.staff_last_name}`,
          staff_id: holiday.staff_id,
          position: requestingStaff?.position || 'Unknown',
          department: requestingStaff?.department || 'Unknown',
          dates: `${holiday.start_date} to ${holiday.end_date}`,
          start_date: holiday.start_date,
          end_date: holiday.end_date,
          days: holiday.days_count,
          type: holiday.type,
          reason: holiday.reason,
          status: holiday.status,
          requested_at: holiday.created_at
        },
        coverage_analysis: {
          total_department_staff: totalDeptStaff,
          staff_available_during_period: staffAvailable,
          staff_on_holiday_during_period: staffOnHoliday,
          coverage_percentage: Math.round(coveragePercentage),
          overlapping_holidays: overlappingHolidays.map((h: any) => ({
            staff: `${h.staff_first_name} ${h.staff_last_name}`,
            dates: `${h.start_date} to ${h.end_date}`,
            type: h.type
          })),
          available_staff_list: sameDeptStaff
            .filter((s: any) => !overlappingHolidays.find((h: any) => h.staff_id === s.id))
            .map((s: any) => ({
              name: `${s.first_name} ${s.last_name}`,
              position: s.position
            }))
        },
        ai_recommendation: {
          decision: recommendation,
          rationale: rationale,
          confidence: coveragePercentage >= 70 ? 'high' : coveragePercentage >= 50 ? 'medium' : 'low'
        }
      };
      
      console.log('[BusinessConsultant] Holiday request detail:', detail);
      return detail;
    } catch (err) {
      console.error('[BusinessConsultant] Holiday detail failed:', err);
      throw err;
    }
  }
  
  /**
   * Approve a holiday request
   */
  private async approveHolidayRequest(holidayId: string, notes?: string): Promise<any> {
    try {
      const staffService = (await import('./staffManagementService')).default;
      
      const updates: any = {
        status: 'Approved'
      };
      
      if (notes) {
        updates.notes = notes;
      }
      
      const result = await staffService.updateHoliday(holidayId, updates);
      
      console.log('[BusinessConsultant] Holiday request approved:', holidayId);
      return {
        success: true,
        message: 'Holiday request approved successfully',
        holiday_id: holidayId,
        data: result.data
      };
    } catch (err) {
      console.error('[BusinessConsultant] Holiday approval failed:', err);
      throw err;
    }
  }
  
  /**
   * Deny a holiday request
   */
  private async denyHolidayRequest(holidayId: string, reason: string): Promise<any> {
    try {
      const staffService = (await import('./staffManagementService')).default;
      
      const updates: any = {
        status: 'Rejected',
        rejection_reason: reason
      };
      
      const result = await staffService.updateHoliday(holidayId, updates);
      
      console.log('[BusinessConsultant] Holiday request denied:', holidayId);
      return {
        success: true,
        message: 'Holiday request denied',
        holiday_id: holidayId,
        reason: reason,
        data: result.data
      };
    } catch (err) {
      console.error('[BusinessConsultant] Holiday denial failed:', err);
      throw err;
    }
  }
  
  /**
   * Mark holiday request for review
   */
  private async markHolidayForReview(holidayId: string, notes?: string): Promise<any> {
    try {
      const staffService = (await import('./staffManagementService')).default;
      
      const reviewNote = notes ? `UNDER REVIEW: ${notes}` : 'UNDER REVIEW: Needs further consideration';
      
      const updates = {
        notes: reviewNote
        // Keep status as Pending
      };
      
      const result = await staffService.updateHoliday(holidayId, updates);
      
      console.log('[BusinessConsultant] Holiday marked for review:', holidayId);
      return {
        success: true,
        message: 'Holiday request marked for review',
        holiday_id: holidayId,
        notes: reviewNote,
        data: result.data
      };
    } catch (err) {
      console.error('[BusinessConsultant] Holiday review marking failed:', err);
      throw err;
    }
  }
  
  /**
   * Get Project Management Report
   */
  private async getProjectManagementReport(reportType: string = 'overview', projectId?: string): Promise<any> {
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const userId = session?.session?.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Get organization ID from user profile
      const { data: profile } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userId)
        .single();
      
      const organizationId = profile?.organization_id;
      
      if (!organizationId) {
        return {
          report_type: reportType,
          message: 'No organization configured. Set up your organization to track projects.',
          stats: { total_projects: 0, active_projects: 0, total_tasks: 0 }
        };
      }
      
      // Fetch project stats
      const statsResponse = await fetch(`${ENV.API_URL}/api/project-management/projects/stats?organization_id=${organizationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!statsResponse.ok) {
        throw new Error('Failed to fetch project stats');
      }
      
      const { data: stats } = await statsResponse.json();
      
      // Fetch projects
      const projectsResponse = await fetch(`${ENV.API_URL}/api/project-management/projects?organization_id=${organizationId}&limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!projectsResponse.ok) {
        throw new Error('Failed to fetch projects');
      }
      
      const { data: projects } = await projectsResponse.json();
      
      // Fetch tasks (upcoming/overdue)
      const tasksResponse = await fetch(`${ENV.API_URL}/api/project-management/tasks/upcoming?organization_id=${organizationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const { data: upcomingTasks } = tasksResponse.ok ? await tasksResponse.json() : { data: [] };
      
      // Build report based on type
      const report: any = {
        report_type: reportType,
        stats: {
          total_projects: stats.total_projects || 0,
          active_projects: stats.active_projects || 0,
          completed_projects: stats.completed_projects || 0,
          total_tasks: stats.total_tasks || 0,
          completed_tasks: stats.completed_tasks || 0,
          total_hours_logged: stats.total_hours || 0,
          projects_at_risk: stats.projects_at_risk || 0
        },
        projects: projects.slice(0, 10).map((p: any) => ({
          id: p.id,
          name: p.name,
          status: p.status,
          priority: p.priority,
          completion: p.completion_percentage || 0,
          due_date: p.due_date,
          owner: p.owner_id,
          tasks_total: p.total_tasks || 0,
          tasks_completed: p.completed_tasks || 0,
          budget: p.budget || 0,
          actual_cost: p.actual_cost || 0
        })),
        upcoming_tasks: upcomingTasks.slice(0, 15).map((t: any) => ({
          id: t.id,
          title: t.title,
          project_id: t.project_id,
          status: t.status,
          priority: t.priority,
          assignee: t.assignee_id,
          due_date: t.due_date,
          is_overdue: new Date(t.due_date) < new Date()
        })),
        risks: {
          overdue_tasks: upcomingTasks.filter((t: any) => new Date(t.due_date) < new Date()).length,
          high_priority_pending: upcomingTasks.filter((t: any) => t.priority === 'urgent' || t.priority === 'high').length,
          projects_at_risk: stats.projects_at_risk || 0
        }
      };
      
      console.log('[BusinessConsultant] PM report generated:', report);
      return report;
    } catch (err) {
      console.error('[BusinessConsultant] PM report failed:', err);
      throw err;
    }
  }
  
  /**
   * Get Risk Assessment
   */
  private async getRiskAssessment(focusArea: string = 'all'): Promise<any> {
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const userId = session?.session?.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Get organization ID
      const { data: profile } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userId)
        .single();
      
      const organizationId = profile?.organization_id;
      
      if (!organizationId) {
        return {
          focus_area: focusArea,
          overall_risk_level: 'low',
          message: 'No organization configured. Risk assessment unavailable.',
          risks: []
        };
      }
      
      const risks: any[] = [];
      let highRiskCount = 0;
      let mediumRiskCount = 0;
      
      // PROJECT RISKS
      if (focusArea === 'all' || focusArea === 'projects') {
        const statsResponse = await fetch(`${ENV.API_URL}/api/project-management/projects/stats?organization_id=${organizationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (statsResponse.ok) {
          const { data: stats } = await statsResponse.json();
          
          if (stats.projects_at_risk > 0) {
            risks.push({
              category: 'projects',
              severity: 'high',
              title: `${stats.projects_at_risk} Project(s) at Risk`,
              description: 'Projects have overdue or blocked tasks',
              recommendation: 'Review project timelines and unblock tasks immediately',
              impact: 'Project delays, missed deadlines'
            });
            highRiskCount++;
          }
          
          // Check project completion rates
          const completionRate = stats.total_tasks > 0 
            ? (stats.completed_tasks / stats.total_tasks) * 100 
            : 0;
          
          if (completionRate < 50 && stats.active_projects > 0) {
            risks.push({
              category: 'projects',
              severity: 'medium',
              title: 'Low Project Completion Rate',
              description: `Only ${Math.round(completionRate)}% of tasks completed`,
              recommendation: 'Review task assignments and remove blockers',
              impact: 'Reduced productivity, project delays'
            });
            mediumRiskCount++;
          }
        }
      }
      
      // RESOURCE RISKS (Staff)
      if (focusArea === 'all' || focusArea === 'resources') {
        const staffService = (await import('./staffManagementService')).default;
        const { data: staff } = await staffService.listStaff();
        const { data: holidays } = await staffService.listHolidays();
        
        const activeStaff = staff.filter((s: any) => s.status === 'active');
        const upcomingHolidays = holidays.filter((h: any) => {
          const startDate = new Date(h.start_date);
          const today = new Date();
          const nextWeek = new Date();
          nextWeek.setDate(today.getDate() + 7);
          return h.status === 'Approved' && startDate >= today && startDate <= nextWeek;
        });
        
        if (upcomingHolidays.length > activeStaff.length * 0.2) {
          risks.push({
            category: 'resources',
            severity: 'medium',
            title: 'High Staff Absence This Week',
            description: `${upcomingHolidays.length} staff on approved leave`,
            recommendation: 'Ensure adequate coverage and adjust workload',
            impact: 'Reduced capacity, potential delays'
          });
          mediumRiskCount++;
        }
      }
      
      // DEADLINE RISKS
      if (focusArea === 'all' || focusArea === 'deadlines') {
        const tasksResponse = await fetch(`${ENV.API_URL}/api/project-management/tasks/upcoming?organization_id=${organizationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (tasksResponse.ok) {
          const { data: upcomingTasks } = await tasksResponse.json();
          const overdueTasks = upcomingTasks.filter((t: any) => new Date(t.due_date) < new Date());
          const urgentTasks = upcomingTasks.filter((t: any) => t.priority === 'urgent' && t.status !== 'done');
          
          if (overdueTasks.length > 0) {
            risks.push({
              category: 'deadlines',
              severity: 'high',
              title: `${overdueTasks.length} Overdue Task(s)`,
              description: 'Tasks past their due date',
              recommendation: 'Prioritize overdue tasks immediately or adjust deadlines',
              impact: 'Project delays, client dissatisfaction'
            });
            highRiskCount++;
          }
          
          if (urgentTasks.length > 5) {
            risks.push({
              category: 'deadlines',
              severity: 'medium',
              title: `${urgentTasks.length} Urgent Tasks Pending`,
              description: 'High number of urgent priority tasks',
              recommendation: 'Review priorities and delegate if possible',
              impact: 'Team stress, quality issues'
            });
            mediumRiskCount++;
          }
        }
      }
      
      // OPERATIONS RISKS
      if (focusArea === 'all' || focusArea === 'operations') {
        // Check CRM pipeline
        const dealsService = (await import('./crm/dealsService')).default;
        const { data: deals } = await dealsService.list({ organization_id: organizationId });
        
        const closingSoonDeals = deals.filter((d: any) => {
          if (!d.expected_close_date) return false;
          const closeDate = new Date(d.expected_close_date);
          const today = new Date();
          const nextWeek = new Date();
          nextWeek.setDate(today.getDate() + 7);
          return closeDate >= today && closeDate <= nextWeek && d.stage !== 'won' && d.stage !== 'lost';
        });
        
        if (closingSoonDeals.length > 0) {
          const totalValue = closingSoonDeals.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);
          risks.push({
            category: 'operations',
            severity: 'medium',
            title: `${closingSoonDeals.length} Deal(s) Closing This Week`,
            description: `£${totalValue.toLocaleString()} in pipeline value needs attention`,
            recommendation: 'Follow up immediately to close deals',
            impact: 'Potential revenue loss'
          });
          mediumRiskCount++;
        }
      }
      
      // Determine overall risk level
      let overallRiskLevel = 'low';
      if (highRiskCount >= 2) {
        overallRiskLevel = 'critical';
      } else if (highRiskCount >= 1) {
        overallRiskLevel = 'high';
      } else if (mediumRiskCount >= 3) {
        overallRiskLevel = 'high';
      } else if (mediumRiskCount >= 1) {
        overallRiskLevel = 'medium';
      }
      
      const assessment = {
        focus_area: focusArea,
        overall_risk_level: overallRiskLevel,
        risk_summary: {
          high_risks: highRiskCount,
          medium_risks: mediumRiskCount,
          low_risks: risks.filter((r: any) => r.severity === 'low').length,
          total_risks: risks.length
        },
        risks: risks,
        recommendations: risks.slice(0, 5).map((r: any) => r.recommendation)
      };
      
      console.log('[BusinessConsultant] Risk assessment generated:', assessment);
      return assessment;
    } catch (err) {
      console.error('[BusinessConsultant] Risk assessment failed:', err);
      throw err;
    }
  }

  /**
   * Edit a message and regenerate response from that point
   */
  async editMessage(messageId: string, newContent: string): Promise<void> {
    try {
      const userId = await this.validateAuthentication();
      
      // Get the message to edit
      const { data: message, error: fetchError } = await supabase
        .from('business_consultant_messages')
        .select('chat_id, created_at, role')
        .eq('id', messageId)
        .single();

      if (fetchError || !message) {
        throw new Error('Message not found');
      }
      
      // Only allow editing user messages
      if (message.role !== 'user') {
        throw new Error('Can only edit user messages');
      }
      
      // Update the message
      const { error: updateError } = await supabase
        .from('business_consultant_messages')
        .update({ 
          content: newContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', messageId);

      if (updateError) {
        throw updateError;
      }
      
      // Delete all messages after this one
      const { error: deleteError } = await supabase
        .from('business_consultant_messages')
        .delete()
        .eq('chat_id', message.chat_id)
        .gt('created_at', message.created_at);
      
      if (deleteError) {
        throw deleteError;
      }
      
      console.log(`[BusinessConsultant] Message edited and subsequent messages deleted`);
    } catch (err) {
      console.error('Error editing message:', err);
      throw new Error('Failed to edit message');
    }
  }

  /**
   * Regenerate AI response for the last user message
   */
  async regenerateResponse(chatId: string): Promise<BusinessConsultantMessage> {
    try {
      // Get last user message
      const messages = await this.getMessagesByChatId(chatId);
      const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
      
      if (!lastUserMessage) {
        throw new Error('No user message found to regenerate from');
      }
      
      // Delete the last assistant message if it exists
      const lastAssistantMessage = messages[messages.length - 1];
      if (lastAssistantMessage && lastAssistantMessage.role === 'assistant') {
        await supabase
          .from('business_consultant_messages')
          .delete()
          .eq('id', lastAssistantMessage.id);
      }
      
      // Get chat to determine business context
      const chat = await this.getChatById(chatId);
      if (!chat) {
        throw new Error('Chat not found');
      }
      
      // Regenerate response
      return await this.sendMessageToChatAndGetResponse(
        chatId,
        lastUserMessage.content,
        !!(chat as any).business_id,
        (chat as any).business_id,
        { skipUserMessageCreate: true }
      );
    } catch (err) {
      console.error('Error regenerating response:', err);
      throw new Error('Failed to regenerate response');
    }
  }

  /**
   * Record user feedback on a message
   */
  async recordFeedback(
    messageId: string,
    sentiment: 'positive' | 'negative',
    reason?: string
  ): Promise<void> {
    try {
      const userId = await this.validateAuthentication();
      
      const { error } = await supabase
        .from('business_consultant_message_feedback')
        .insert({
          message_id: messageId,
          user_id: userId,
          sentiment,
          reason,
          created_at: new Date().toISOString()
        });

      if (error) {
        // Check if table exists, if not just log warning
        if (error.message.includes('relation') && error.message.includes('does not exist')) {
          console.warn('[BusinessConsultant] Feedback table not yet created. Run migration to enable feedback.');
          return;
        }
        throw error;
      }
      
      console.log(`[BusinessConsultant] Feedback recorded: ${sentiment}`);
    } catch (err) {
      console.error('Error recording feedback:', err);
      // Don't throw - feedback is non-critical
    }
  }
}

/**
 * Singleton instance of the Business Consultant Service
 */
export const businessConsultantService = new BusinessConsultantService();
